<template>
  <s-modal
    v-model="openCalculateModal"
    class="calculate-losses-modal"
    size="lg"
    cancel-confirm-message-code="MC006"
    ok-confirm-message-code="MC011"
    confirm-type="confirmationTypes.YES_NO"
    :title="$t('asset-information.asset-info.losses.modal.title')"
    :ok-title="$t('asset-information.asset-info.losses.modal.calculate')"
    :ok-disabled="!isValid"
    @ok="calculate"
    @cancel="close"
  >
    <b-container class="p-0">
      <div class="border p-2 rounded mb-4">
        <label>{{ $t('asset-information.asset-info.losses.modal.applied-power') }}:</label>
        <b-row class="mt-4 mb-2">
          <b-col
            xs="12"
            sm="4"
          >
            <label>
              {{ $t('asset-information.asset-info.losses.modal.applied-potency-phx-test') }}*
              <s-icon
                name="hint"
                v-b-tooltip.hover.right
                :title="$t('asset-information.asset-info.engineering-modules-information')"
              />
            </label>
            <s-input-label-edit
              v-model="$v.appliedPotencyPhxTest.$model"
              :formatted-value="formatNumberValue(appliedPotencyPhxTest, 3)"
              :editable="true"
            >
              <s-number-input
                class="text-right"
                v-model="$v.appliedPotencyPhxTest.$model"
                :numeric-options="configureNumericOptionsPrecision(3)"
                :validation="$v.appliedPotencyPhxTest"
                :invalid="$v.appliedPotencyPhxTest.$error"
              />
              <b-form-invalid-feedback>
                {{ getRangeValueMessage() }}
              </b-form-invalid-feedback>
            </s-input-label-edit>
          </b-col>
          <b-col
            xs="12"
            sm="4"
          >
            <label>
              {{ $t('asset-information.asset-info.losses.modal.applied-potency-phy-test') }}*
              <s-icon
                name="hint"
                v-b-tooltip.hover.right
                :title="$t('asset-information.asset-info.engineering-modules-information')"
              />
            </label>
            <s-input-label-edit
              v-model="$v.appliedPotencyPhyTest.$model"
              :formatted-value="formatNumberValue(appliedPotencyPhyTest, 3)"
              :editable="true"
            >
              <s-number-input
                class="text-right"
                v-model="$v.appliedPotencyPhyTest.$model"
                :numeric-options="configureNumericOptionsPrecision(3)"
                :validation="$v.appliedPotencyPhyTest"
                :invalid="$v.appliedPotencyPhyTest.$error"
              />
              <b-form-invalid-feedback>
                {{ getRangeValueMessage() }}
              </b-form-invalid-feedback>
            </s-input-label-edit>
          </b-col>
          <b-col
            xs="12"
            sm="4"
          >
            <label>
              {{ $t('asset-information.asset-info.losses.modal.applied-potency-pxy-test') }}*
              <s-icon
                name="hint"
                v-b-tooltip.hover.right
                :title="$t('asset-information.asset-info.engineering-modules-information')"
              />
            </label>
            <s-input-label-edit
              v-model="$v.appliedPotencyPxyTest.$model"
              :formatted-value="formatNumberValue(appliedPotencyPxyTest, 3)"
              :editable="true"
            >
              <s-number-input
                class="text-right"
                v-model="$v.appliedPotencyPxyTest.$model"
                :numeric-options="configureNumericOptionsPrecision(3)"
                :validation="$v.appliedPotencyPxyTest"
                :invalid="$v.appliedPotencyPxyTest.$error"
              />
              <b-form-invalid-feedback>
                {{ getRangeValueMessage() }}
              </b-form-invalid-feedback>
            </s-input-label-edit>
          </b-col>
        </b-row>
      </div>
      <div class="border p-2 rounded">
        <label>{{ $t('asset-information.asset-info.losses.modal.copper-losses') }}:</label>
        <b-row class="mt-4">
          <b-col
            xs="12"
            sm="4"
          >
            <label>
              {{ $t('asset-information.asset-info.losses.modal.copper-losses-phx-test') }}*
              <s-icon
                name="hint"
                v-b-tooltip.hover.right
                :title="$t('asset-information.asset-info.engineering-modules-information')"
              />
            </label>
            <s-input-label-edit
              v-model="$v.copperLossesPhxTest.$model"
              :formatted-value="formatNumberValue(copperLossesPhxTest, 3)"
              :editable="true"
            >
              <s-number-input
                class="text-right"
                v-model="$v.copperLossesPhxTest.$model"
                :numeric-options="configureNumericOptionsPrecision(3)"
                :validation="$v.copperLossesPhxTest"
                :invalid="$v.copperLossesPhxTest.$error"
              />
              <b-form-invalid-feedback>
                {{ getRangeValueMessage() }}
              </b-form-invalid-feedback>
            </s-input-label-edit>
          </b-col>
          <b-col
            xs="12"
            sm="4"
          >
            <label>
              {{ $t('asset-information.asset-info.losses.modal.copper-losses-phy-test') }}*
              <s-icon
                name="hint"
                v-b-tooltip.hover.right
                :title="$t('asset-information.asset-info.engineering-modules-information')"
              />
            </label>
            <s-input-label-edit
              v-model="$v.copperLossesPhyTest.$model"
              :formatted-value="formatNumberValue(copperLossesPhyTest, 3)"
              :editable="true"
            >
              <s-number-input
                class="text-right"
                v-model="$v.copperLossesPhyTest.$model"
                :numeric-options="configureNumericOptionsPrecision(3)"
                :validation="$v.copperLossesPhyTest"
                :invalid="$v.copperLossesPhyTest.$error"
              />
              <b-form-invalid-feedback>
                {{ getRangeValueMessage() }}
              </b-form-invalid-feedback>
            </s-input-label-edit>
          </b-col>
          <b-col
            xs="12"
            sm="4"
          >
            <label>
              {{ $t('asset-information.asset-info.losses.modal.copper-losses-pxy-test') }}*
              <s-icon
                name="hint"
                v-b-tooltip.hover.right
                :title="$t('asset-information.asset-info.engineering-modules-information')"
              />
            </label>
            <s-input-label-edit
              v-model="$v.copperLossesPxyTest.$model"
              :formatted-value="formatNumberValue(copperLossesPxyTest, 3)"
              :editable="true"
            >
              <s-number-input
                class="text-right"
                v-model="$v.copperLossesPxyTest.$model"
                :numeric-options="configureNumericOptionsPrecision(3)"
                :validation="$v.copperLossesPxyTest"
                :invalid="$v.copperLossesPxyTest.$error"
              />
              <b-form-invalid-feedback>
                {{ getRangeValueMessage() }}
              </b-form-invalid-feedback>
            </s-input-label-edit>
          </b-col>
        </b-row>
      </div>
    </b-container>
  </s-modal>
</template>

<script>
import LossesService from '@/domain/services/installation/assetInfo/losses';
import { between, required } from 'vuelidate/lib/validators';
import cloneDeep from 'lodash/cloneDeep';
import ConfirmationTypes from '@/constants/confirmationTypes';

const rangeRn17 = { min: 0.001, max: 100000000.0 };

export default {
  name: 'CalculateLossesModal',
  props: {
    value: {
      type: Boolean,
    },
    losses: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    appliedPotencyPhxTest: null,
    appliedPotencyPhyTest: null,
    appliedPotencyPxyTest: null,
    copperLossesPhxTest: null,
    copperLossesPhyTest: null,
    copperLossesPxyTest: null,
    confirmationTypes: ConfirmationTypes,
  }),
  validations: {
    appliedPotencyPhxTest: {
      required,
      between: between(rangeRn17.min, rangeRn17.max),
    },
    appliedPotencyPhyTest: {
      required,
      between: between(rangeRn17.min, rangeRn17.max),
    },
    appliedPotencyPxyTest: {
      required,
      between: between(rangeRn17.min, rangeRn17.max),
    },
    copperLossesPhxTest: {
      required,
      between: between(rangeRn17.min, rangeRn17.max),
    },
    copperLossesPhyTest: {
      required,
      between: between(rangeRn17.min, rangeRn17.max),
    },
    copperLossesPxyTest: {
      required,
      between: between(rangeRn17.min, rangeRn17.max),
    },
  },
  computed: {
    options() {
      return {
        appliedPotencyPhxTest: {
          rangeRn17,
          maxLength: 15,
          state: this.getValidationState(this.$v.appliedPotencyPhxTest),
          disabled: this.saving,
        },
        appliedPotencyPhyTest: {
          rangeRn17,
          maxLength: 15,
          state: this.getValidationState(this.$v.appliedPotencyPhyTest),
          disabled: this.saving,
        },
        appliedPotencyPxyTest: {
          rangeRn17,
          maxLength: 15,
          state: this.getValidationState(this.$v.appliedPotencyPxyTest),
          disabled: this.saving,
        },
        copperLossesPhxTest: {
          rangeRn17,
          maxLength: 15,
          state: this.getValidationState(this.$v.copperLossesPhxTest),
          disabled: this.saving,
        },
        copperLossesPhyTest: {
          rangeRn17,
          maxLength: 15,
          state: this.getValidationState(this.$v.copperLossesPhyTest),
          disabled: this.saving,
        },
        copperLossesPxyTest: {
          rangeRn17,
          maxLength: 15,
          state: this.getValidationState(this.$v.copperLossesPxyTest),
          disabled: this.saving,
        },
      };
    },
    openCalculateModal: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    isValid() {
      return !this.$v.$invalid;
    },
  },
  methods: {
    getValidationState(value) {
      const modelValue = value.$model;
      const isValid = !value.$invalid;
      if (modelValue != null && !isValid) {
        return false;
      }
      return null;
    },
    configureNumericOptionsPrecision(precision = 0) {
      return {
        decimalPlaces: precision,
      };
    },
    formatNumberValue(value, decimals = 0) {
      if (typeof value !== 'number') {
        return value;
      }
      return this.$n(value, {
        minimumFractionDigits: 0,
        maximumFractionDigits: decimals,
      });
    },
    getRangeValueMessage(precision = 3) {
      const numberFormat = {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      };
      return this.$t('messages.MA086.message', {
        minValue: this.$n(rangeRn17.min, numberFormat),
        maxValue: this.$n(rangeRn17.max, numberFormat),
      });
    },
    calculate() {
      this.calculateLosses();
      this.$emit('input', false);
    },
    close() {
      this.setDefaultValues();
      this.$emit('input', false);
    },
    mountPayload() {
      return {
        appliedPotencyPhxTest: this.appliedPotencyPhxTest,
        appliedPotencyPhyTest: this.appliedPotencyPhyTest,
        appliedPotencyPxyTest: this.appliedPotencyPxyTest,
        copperLossesPhxTest: this.copperLossesPhxTest,
        copperLossesPhyTest: this.copperLossesPhyTest,
        copperLossesPxyTest: this.copperLossesPxyTest,
      };
    },
    calculateLosses() {
      const { assetId } = this.$route.params;
      this.$emit('loading', true);
      LossesService.calculateLosses(this.mountPayload(), assetId).then((response) => {
        const losses = cloneDeep(this.losses);
        losses.winding1ShortCircuitLosses = response.winding1ShortCircuitLosses;
        losses.winding2ShortCircuitLosses = response.winding2ShortCircuitLosses;
        losses.winding3ShortCircuitLosses = response.winding3ShortCircuitLosses;
        this.$emit('update:losses', losses);
        this.$pushAlert({ code: 'MS013' });
      }).catch(() => {
        this.$emit('input', true);
      }).finally(() => {
        this.$emit('loading', false);
      });
    },
    isAnyFieldFilled() {
      return (
        this.appliedPotencyPhxTest
        || this.appliedPotencyPhyTest
        || this.appliedPotencyPxyTest
        || this.copperLossesPxyTest
        || this.copperLossesPhxTest
        || this.copperLossesPhyTest
      );
    },
    setDefaultValues() {
      this.appliedPotencyPhxTest = null;
      this.appliedPotencyPhyTest = null;
      this.appliedPotencyPxyTest = null;
      this.copperLossesPxyTest = null;
      this.copperLossesPhxTest = null;
      this.copperLossesPhyTest = null;
    },
  },
};
</script>

<style lang="sass" scoped>
.calculate-losses-modal
  &__title
    font-weight: bold
</style>
